import { Icon, Text } from '@cotera/client/app/components/ui';
import { IconName, Tooltip } from '@cotera/client/app/components';
import React, { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { cn } from '../../lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Column, HeaderComponentProps } from './types';
import { DragItem, DropResult } from './use-column-reorder';
import { Button } from '../ui/button';

export const DefaultHeader: React.FC<
  HeaderComponentProps & {
    className?: string;
    actions?: React.ReactNode;
    quickActions?: React.ReactNode;
    title?: React.ReactNode;
    onClick?: () => void;
  }
> = ({
  column,
  onPinColumn,
  onUnpinColumn,
  children,
  actions,
  quickActions,
  className,
  title,
  onClick,
}) => {
  const shouldShowHover = column.t !== 'assumption';

  return (
    <div className="px-3 py-2 font-medium truncate flex justify-between relative overflow-hidden">
      <div className="flex flex-col gap-1 w-full overflow-hidden">
        <div
          onClick={onClick}
          className={cn(
            'flex items-center justify-start group pb-2',
            className
          )}
        >
          <Tooltip
            side="auto"
            tooltipContent={
              <div className="max-w-[300px] max-h-[150px] flex flex-col gap-1 overflow-auto">
                <Text.Caption>Type</Text.Caption>
                {column.type}
              </div>
            }
          >
            <div
              className={cn(
                'flex items-center font-normal text-regular transition-colors [&>*]:transition-colors',
                shouldShowHover
                  ? 'text-primary hover:text-muted-foreground hover:[&>*]:text-muted-foreground cursor-pointer'
                  : 'text-primary'
              )}
            >
              <Icon icon={column.icon as IconName} className="mr-2" />
              {title ?? column.title}
            </div>
          </Tooltip>
        </div>
        {children}
      </div>
      <div className="flex px-2 py-2 gap-1.5 absolute right-0 top-0">
        {quickActions}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant={'ghost'}
              size={'sm'}
              icon="menu"
              className="!outline-hidden !py-0 !px-0 !h-fit mt-1 !bg-white"
            />
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            {column.pinned ? (
              <DropdownMenuItem asChild>
                <Button
                  variant={'ghost'}
                  className="w-full justify-start"
                  size={'sm'}
                  icon="pin-off"
                  onClick={() => {
                    onUnpinColumn(column.key);
                  }}
                >
                  UnPin
                </Button>
              </DropdownMenuItem>
            ) : (
              <>
                <DropdownMenuItem asChild>
                  <Button
                    variant={'ghost'}
                    size={'sm'}
                    icon="pin"
                    className="w-full justify-start"
                    onClick={() => {
                      onPinColumn(column.key, 'left');
                    }}
                  >
                    Pin Left
                  </Button>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Button
                    className="w-full justify-start"
                    variant={'ghost'}
                    size={'sm'}
                    icon="pin"
                    onClick={() => {
                      onPinColumn(column.key, 'right');
                    }}
                  >
                    Pin Right
                  </Button>
                </DropdownMenuItem>
              </>
            )}
            {actions !== undefined && actions}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

// Draggable column header component
interface DraggableColumnHeaderProps {
  column: Column & { x: number; width: number };
  index: number;
  group: 'leftPinned' | 'regular' | 'rightPinned';
  onMoveColumn: (dragItem: DragItem, dropResult: DropResult) => void;
  onPinColumn: (columnKey: string, side: 'left' | 'right') => void;
  onUnpinColumn: (columnKey: string) => void;
  isLastInGroup: boolean;
  onResizeStart: (e: React.MouseEvent, columnKey: string) => void;
  resizingColumn: string | null;
  height: number;
  HeaderComponent: React.ComponentType<HeaderComponentProps>;
  registerRef: (id: string, element: HTMLElement | null) => void;
}

export const DraggableColumnHeader = ({
  column,
  index,
  group,
  onMoveColumn,
  onPinColumn,
  onUnpinColumn,
  isLastInGroup,
  onResizeStart,
  resizingColumn,
  HeaderComponent = DefaultHeader,
  height = 40,
  registerRef,
}: DraggableColumnHeaderProps) => {
  const ref = useRef<HTMLDivElement>(null);

  const [{ isDragging }, drag] = useDrag({
    type: 'COLUMN',
    item: { columnKey: column.key, index, sourceGroup: group },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [{ isOver }, drop] = useDrop({
    accept: 'COLUMN',
    hover: (draggedItem: DragItem, monitor) => {
      if (!ref.current) {
        return;
      }

      const dragIndex = draggedItem.index;
      const hoverIndex = index;
      const dragGroup = draggedItem.sourceGroup;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex && dragGroup === group) {
        return;
      }

      // Only perform the move when the mouse has crossed half of the items height
      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      const hoverMiddleX =
        (hoverBoundingRect.right - hoverBoundingRect.left) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientX = clientOffset!.x - hoverBoundingRect.left;

      // Dragging leftwards
      if (dragIndex > hoverIndex && hoverClientX > hoverMiddleX) {
        return;
      }

      // Dragging rightwards
      if (dragIndex < hoverIndex && hoverClientX < hoverMiddleX) {
        return;
      }

      // Time to actually perform the action
      onMoveColumn(draggedItem, {
        targetIndex: hoverIndex,
        targetGroup: group,
      });

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      draggedItem.index = hoverIndex;
      draggedItem.sourceGroup = group;
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  drag(drop(ref));

  return (
    <div
      ref={ref}
      className={`flex-shrink-0 border-r border-gray-200 flex justify-between relative ${
        isDragging ? 'opacity-50' : ''
      } ${isOver ? 'bg-blue-100' : ''}`}
      style={{ width: column.width, height }}
    >
      <div
        className="flex flex-1 min-w-0 items-center"
        ref={(el) => registerRef(column.key, el)}
        data-item-id={column.key}
      >
        <div className="flex-1 min-w-0">
          <HeaderComponent
            column={column}
            onPinColumn={onPinColumn}
            onUnpinColumn={onUnpinColumn}
          />
        </div>
      </div>

      {/* Resize handle */}
      {!isLastInGroup && (
        <div
          className={`absolute right-0 top-0 w-1 h-full cursor-col-resize hover:bg-blue-300 ${
            resizingColumn === column.key ? 'bg-blue-500' : 'bg-transparent'
          } z-20`}
          onMouseDown={(e) => onResizeStart(e, column.key)}
          title="Drag to resize column"
        />
      )}
    </div>
  );
};
